import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { UserMembershipTypeDto } from '../../../models/user-membership/dto/user-membership.dto';
import { GET_USER_MEMBERSHIP_TYPES_QUERY_KEY } from './useGetUserMembershipTypes';

/**
 * DELETE user-membership-types/:typeId/variants/:variantId — soft-delete
 * (delete-membership-variant.ts). The API refuses (400) when the variant is
 * the type's current live default, or has any non-terminal-status live
 * subscription — the caller surfaces those via useErrorToast, this hook only
 * updates the cache on an actual success.
 */
export const useDeleteMembershipVariant = (
  organizationId: string,
  membershipTypeId: string,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteMembershipVariant', membershipTypeId],
    mutationFn: (variantId: string) =>
      apiClient.fetchApi<{ success: true }, {}>({
        method: 'DELETE',
        endpoint: `user-membership-types/${membershipTypeId}/variants/${variantId}`,
      }),
    onSuccess: (_data, variantId) => {
      queryClient.setQueryData<UserMembershipTypeDto[]>(
        GET_USER_MEMBERSHIP_TYPES_QUERY_KEY(organizationId),
        (previous) => {
          if (!previous) return previous;
          return previous.map((type) => {
            if (type.id !== membershipTypeId) return type;
            return {
              ...type,
              variants: (type.variants ?? []).filter(
                (v) => v.id !== variantId,
              ),
            };
          });
        },
      );
    },
  });
};
